/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { CalendarDateRequired } from '#utils/UNSAFE_calendarDateUtils';
import { DateISOStr } from '#UNSAFE_IntlDateTime';
import { FormatterFunc } from '#UNSAFE_IntlFormatParse';
import { getIsoDateStr, toJSDate } from '#utils/UNSAFE_calendarDateUtils';
import { ComponentProps, useCallback } from 'preact/compat';

import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '#resources/nls/bundle';
import { useFocusableCell } from '#UNSAFE_DatePicker/useFocusableCell';
import { useCellNavigation } from '#UNSAFE_DatePicker/useCellNavigation';
import { StyledDatePickerButton } from '#PRIVATE_StyledDatePickerButton';

type NavigationDirections =
  | 'previousDay'
  | 'nextDay'
  | 'previousWeek'
  | 'nextWeek'
  | 'previousMonth'
  | 'nextMonth'
  | 'previousYear'
  | 'nextYear'
  | 'firstDayOfMonth'
  | 'lastDayOfMonth'
  | 'today';

export type DayFormatOptions = {
  state: 'enabled' | 'disabled' | 'restricted';
};

// These are the properties the useDayCell hook takes.
export type UseDayCellProps = {
  /**
   * Date of the day cell. This will be formatted as a full date and put in the aria-label.
   */
  date: CalendarDateRequired;
  /**
   * A function used to format the day cell date as a full date.
   */
  dateFormatter: FormatterFunc<DateISOStr>;
  /**
   * A function that receives a date and returns an object specifying how to format the provided date.
   * This function is called for each date that is rendered and this can be used to control how individual
   * dates are rendered - disabled, restricted, or enabled.
   * @param date The date to format
   * @returns The format options
   */
  dayFormatter?: (date: CalendarDateRequired) => DayFormatOptions;
  /**
   * Whether the day cell is in an adjacent month, not the current month.
   */
  isAdjacentMonth: boolean;
  /**
   * Whether the day cell is disabled, like if it is out of the min/max range.
   */
  isDisabled?: boolean;
  /**
   * Whether the day cell is focusable, meaning it will have tabIndex 0.
   */
  isFocusable: boolean;
  /**
   * Whether the day cell has focus.
   */
  isFocused: boolean;
  /**
   * Whether the day cell is hidden. For example, days outside of the current month are hidden by default.
   */
  isHidden: boolean;
  /**
   * Whether the day cell is readonly. It is not activeable.
   */
  isReadonly: boolean;
  /**
   * Whether the day cell looks selected.
   */
  isSelected: boolean;
  /**
   * Whether the day cell's date is today.
   */
  isToday: boolean;
  /**
   * A callback that will be invoked when the user selects the date by clicking on a day cell or selecting the enter or space key on a day cell.
   * @param payload The payload containing the date represented by this cell.
   */
  onAction: (payload: { date: CalendarDateRequired }) => void;

  /**
   * A callback that will be invoked when the user navigates.
   */
  onNavigation: (detail: { direction: NavigationDirections; date: CalendarDateRequired }) => void;
};

/**
 * The useDayCell hook returns properties for an individual DayCell. The properties returned should be spread on the StyledDatePickerButton.
 * These include the event handlers for keydown and click.
 */
export const useDayCell = ({
  date,
  dayFormatter = defaultDayFormatter,
  dateFormatter,
  isAdjacentMonth,
  isDisabled,
  isFocusable,
  isFocused,
  isHidden,
  isReadonly,
  isSelected,
  isToday,
  onAction,
  onNavigation
}: UseDayCellProps) => {
  // apply day formatting
  const { state } = dayFormatter(date);
  const isDisabledFormat = state === 'disabled';
  const isRestrictedFormat = state === 'restricted';

  // Focus the day cell with isFocused true when the state updates.
  const buttonProps = useFocusableCell({ isFocusable, isFocused });
  // Override onAction to not call it if disabled, readonly or restricted
  const handleAction: UseDayCellProps['onAction'] = useCallback(
    (payload) => {
      if (isDisabled || isReadonly || isDisabledFormat || isRestrictedFormat) return;
      onAction(payload);
    },
    [isDisabled, isDisabledFormat, isReadonly, isRestrictedFormat, onAction]
  );
  const buttonEventHandlers = useCellNavigation({
    navigationMap: {
      ArrowLeft: { ltr: 'previousDay', rtl: 'nextDay' },
      ArrowRight: { ltr: 'nextDay', rtl: 'previousDay' },
      ArrowDown: 'nextWeek',
      ArrowUp: 'previousWeek',
      PageDown: 'nextMonth',
      PageUp: 'previousMonth',
      Home: 'firstDayOfMonth',
      End: 'lastDayOfMonth',
      ShiftPageDown: 'nextYear',
      ShiftPageUp: 'previousYear',
      CtrlAltT: 'today'
    },
    payload: { date },
    onAction: handleAction,
    onNavigation
  });

  // For accessibility, we want a full date.
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const today = translations.datePicker_today();
  const selected = translations.datePicker_selected();
  const restricted = translations.datePicker_restricted();

  const dateIsoStr = getIsoDateStr(date.year, date.month, date.day);
  const formattedDate = dateFormatter(dateIsoStr);
  let ariaLabel = isToday ? `${today}, ${formattedDate}` : formattedDate;
  ariaLabel = isRestrictedFormat ? `${ariaLabel}, ${restricted}` : ariaLabel;
  ariaLabel = isSelected ? `${ariaLabel}, ${selected}` : ariaLabel;
  const dayOfMonth = toJSDate(date.year, date.month, date.day).getDate().toString();

  return {
    buttonProps: {
      'aria-label': ariaLabel,
      ...((isHidden || isDisabled || isDisabledFormat || isRestrictedFormat) && {
        'aria-disabled': true
      }),
      isActivable: !(isDisabled || isReadonly || isDisabledFormat || isRestrictedFormat),
      isAdjacentMonth,
      isDimmed: isDisabled || isDisabledFormat,
      isHidden,
      isHoverable: !(isDisabled || isReadonly || isDisabledFormat || isRestrictedFormat),
      isReadonly,
      isSelected,
      isRestricted: isRestrictedFormat,
      isToday,
      label: dayOfMonth,
      ...buttonProps,
      ...buttonEventHandlers
    } as ComponentProps<typeof StyledDatePickerButton>
  };
};

/**
 * A default day formatter that always formats as enabled state.
 */
const defaultDayFormatter = () => ({ state: 'enabled' }) as DayFormatOptions;
